/**
 * Plugin Permissions and Manifest Types
 *
 * Vulnerability #3 (P0 CRITICAL): Runtime plugin-to-plugin isolation
 *
 * Security Model: Capability-Based Security
 * - Plugins declare required permissions in plugin.json manifest
 * - Runtime enforcement via PluginSandbox validates all operations
 * - Deny-by-default: If not declared, access is blocked
 */

/**
 * Database operation types
 */
export type DatabaseOperation = 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE';

/**
 * Redis operation types
 */
export type RedisOperation = 'READ' | 'WRITE';

/**
 * Plugin permissions structure
 */
export interface PluginPermissions {
  /**
   * Database table access permissions
   * Format: "table_name:OPERATION"
   * Example: ["plugin_analytics_events:SELECT", "plugin_analytics_events:INSERT"]
   */
  database?: string[];

  /**
   * Redis key access permissions
   * Format: "key_pattern:OPERATION"
   * Example: ["analytics:*:READ", "cache:analytics:*:WRITE"]
   */
  redis?: string[];

  /**
   * QStash worker publish permissions
   * Format: "publish:worker_name"
   * Example: ["publish:analytics-worker", "publish:email-worker"]
   */
  qstash?: string[];

  /**
   * HTTP request allowlist (domain patterns)
   * Format: "https://domain.com/*"
   * Example: ["https://api.posthog.com/*", "https://api.stripe.com/*"]
   */
  http?: string[];

  /**
   * Environment variable access (allowlist)
   * Format: "ENV_VAR_NAME"
   * Example: ["POSTHOG_API_KEY", "ANALYTICS_ENDPOINT"]
   */
  env?: string[];
}

/**
 * Plugin manifest structure (plugin.json)
 */
export interface PluginManifest {
  /**
   * Unique plugin identifier
   * Format: "@dramadetector/plugin-{name}"
   */
  id: string;

  /**
   * Plugin display name
   */
  name: string;

  /**
   * Plugin version (semver)
   */
  version: string;

  /**
   * Plugin classification (determines quota-engine access)
   */
  classification: 'core' | 'infrastructure' | 'feature';

  /**
   * Required permissions (capability-based security)
   */
  permissions: PluginPermissions;

  /**
   * Plugin dependencies (other plugins or core modules)
   */
  dependencies?: {
    core?: string[];
    infrastructure?: string[];
    plugins?: string[];
  };
}

/**
 * Plugin isolation violation error
 * MEDIUM-2 FIX (2025-12-08): Separate public/internal messages for security
 */
export class PluginIsolationError extends Error {
  /** Detailed message for audit logs (includes sensitive implementation details) */
  public readonly detailedMessage: string;

  constructor(
    public pluginId: string,
    public violationType: 'import' | 'database' | 'redis' | 'qstash' | 'http' | 'env',
    detailedMessage: string
  ) {
    // Public message: Generic, no implementation details (prevents information disclosure)
    const genericMessages: Record<typeof violationType, string> = {
      import: 'Unauthorized module import attempted',
      database: 'Unauthorized database operation attempted',
      redis: 'Unauthorized cache operation attempted',
      qstash: 'Unauthorized queue operation attempted',
      http: 'Unauthorized HTTP request attempted',
      env: 'Unauthorized environment variable access attempted',
    };

    super(`[${pluginId}] Plugin isolation violation: ${genericMessages[violationType]}`);
    this.name = 'PluginIsolationError';

    // Store detailed message for audit logging (not exposed to plugin)
    this.detailedMessage = detailedMessage;
  }
}

/**
 * Plugin Error Classification System
 *
 * Week 5 Decision Gate Enhancement (Phase 4 Fix)
 * Multi-Model Consensus (2025-12-09):
 * - Gemini 3 Pro: 9/10 confidence → Option B (Typed Errors)
 * - Claude Sonnet 4.5: 9/10 confidence → Enhanced Option B
 * - O3 Mini: 9/10 confidence → Option A (minority)
 *
 * Final Decision: Enhanced Option B (2/3 vote)
 * - Enables distinction between config vs runtime failures
 * - Prevents silent swallowing of real errors
 * - Improves Week 5 Decision Gate telemetry accuracy
 */

/**
 * Plugin error codes for typed error handling
 *
 * Error Classification:
 * - MISSING_CONFIG: Optional service not configured (info, not error)
 * - INVALID_CONFIG: Configuration malformed (error)
 * - INITIALIZATION_FAILED: Provider crashed (error)
 * - RUNTIME_ERROR: Operation failed (error)
 */
export enum PluginErrorCode {
  /** API key or configuration not provided (optional service) */
  MISSING_CONFIG = 'MISSING_CONFIG',

  /** Configuration provided but invalid format/value */
  INVALID_CONFIG = 'INVALID_CONFIG',

  /** Provider initialization failed (crash, network error) */
  INITIALIZATION_FAILED = 'INITIALIZATION_FAILED',

  /** Operation failed at runtime (after successful init) */
  RUNTIME_ERROR = 'RUNTIME_ERROR',
}

/**
 * Plugin error with error code classification
 *
 * Enables wrappers to distinguish between:
 * - MISSING_CONFIG → Silent success (optional services)
 * - Other codes → Re-throw as real errors
 *
 * @example
 * ```typescript
 * // Provider throws typed error
 * if (!POSTHOG_KEY) {
 *   throw new PluginError(
 *     'PostHog API key not configured',
 *     PluginErrorCode.MISSING_CONFIG,
 *     '@dramadetector/plugin-analytics',
 *     'info'
 *   );
 * }
 *
 * // Wrapper filters typed errors
 * try {
 *   await plugin.initialize();
 * } catch (error) {
 *   if (error instanceof PluginError && error.code === PluginErrorCode.MISSING_CONFIG) {
 *     console.info(`[${error.pluginName}] ${error.message}`);
 *     return; // Silent success for optional config
 *   }
 *   throw error; // Re-throw real errors
 * }
 * ```
 */
export class PluginError extends Error {
  constructor(
    message: string,
    public code: PluginErrorCode,
    public pluginName: string,
    public severity: 'info' | 'warning' | 'error' = 'error'
  ) {
    super(message);
    this.name = 'PluginError';
  }
}

/**
 * Plugin Health Monitoring Event Schemas
 *
 * Week 5 Decision Gate instrumentation (Phase 1)
 * - Runtime-only events (NOT build-time)
 * - PostHog SDK integration for browser/React Native context
 * - OWASP A09 compliance via stack trace sanitization
 *
 * Multi-Model Consensus (2025-12-09):
 * - Gemini 3 Pro: 9/10 confidence for runtime-only
 * - O3 Mini: 9/10 confidence for runtime-only
 * - Claude Sonnet 4.5: 9/10 confidence for runtime-only
 */

/**
 * Plugin execution platform
 */
export type PluginPlatform = 'web' | 'ios' | 'android' | 'build';

/**
 * Plugin initialization event
 *
 * Tracks successful plugin initialization in runtime context.
 * Captured by plugin loader (apps/mobile/lib/plugin-loader.ts).
 *
 * Week 5 Value: Success rate monitoring (debugging support)
 */
export interface PluginInitializedEvent {
  /** Plugin identifier (e.g., "plugin-analytics") */
  plugin_name: string;

  /** Initialization duration in milliseconds */
  init_duration_ms: number;

  /** Plugin version (semver) */
  version: string;

  /** Execution platform */
  platform: PluginPlatform;
}

/**
 * Plugin error event
 *
 * Tracks plugin failures with sanitized stack traces (OWASP A09).
 * Critical for Week 5 Decision Gate experiment validity.
 *
 * Week 5 Value: Debugging support for productivity measurement
 */
export interface PluginErrorEvent {
  /** Plugin identifier (e.g., "plugin-email") */
  plugin_name: string;

  /** Error classification */
  error_type: 'initialization' | 'runtime' | 'contract_violation';

  /** Error message (user-safe, no PII) */
  error_message: string;

  /** Sanitized stack trace (max 500 chars, PII removed) */
  stack_trace?: string;

  /** Operation that failed (optional context) */
  operation?: string;
}

/**
 * Plugin bundle size event
 *
 * DEFERRED: Phase 1 tracks via CI/CD, not runtime.
 * Included for completeness (post-Week 5 Decision Gate).
 *
 * CI/CD Value: Build-time bundle analysis, not runtime instrumentation
 */
export interface PluginBundleSizeEvent {
  /** Plugin identifier */
  plugin_name: string;

  /** Raw bundle size in kilobytes */
  bundle_size_kb: number;

  /** Compressed (gzip) size in kilobytes */
  compressed_size_kb: number;

  /** Target platform */
  platform: PluginPlatform;
}

/**
 * Plugin operation event
 *
 * DEFERRED: Post-Week 5 Decision Gate (performance overhead risk).
 * Included for completeness.
 *
 * Post-Week 5 Value: Operational monitoring, batch performance
 */
export interface PluginOperationEvent {
  /** Plugin identifier */
  plugin_name: string;

  /** Operation name (e.g., "event_batch_send") */
  operation: string;

  /** Operation duration in milliseconds */
  duration_ms: number;

  /** Operation success status */
  success: boolean;

  /** Batch size (if applicable) */
  batch_size?: number;
}

/**
 * Sanitize stack trace for analytics (OWASP A09 compliance)
 *
 * Removes:
 * - User paths (/home/USER, /Users/USER, C:\Users\USER)
 * - Environment variables (.env references)
 * - Sensitive keywords (password, secret, token, api_key)
 * - Process.env / Deno.env access patterns
 *
 * Limits:
 * - Max 500 characters (prevent performance impact)
 *
 * @param stack - Raw stack trace string
 * @returns Sanitized stack trace (safe for PostHog)
 *
 * @example
 * ```typescript
 * try {
 *   await plugin.initialize();
 * } catch (error) {
 *   analytics.capture('Plugin_Error', {
 *     plugin_name: 'plugin-analytics',
 *     error_type: 'initialization',
 *     error_message: error.message,
 *     stack_trace: sanitizeStackTrace(error.stack),
 *   });
 * }
 * ```
 */
export function sanitizeStackTrace(stack: string | undefined): string {
  if (!stack) return '';

  return stack
    .split('\n')
    .map(line => {
      // Replace user-specific paths
      return line
        .replace(/\/home\/[^/]+/g, '/home/USER')
        .replace(/\/Users\/[^/]+/g, '/Users/USER')
        .replace(/C:\\Users\\[^\\]+/g, 'C:\\Users\\USER')
        // Redact sensitive files
        .replace(/\.env\b/gi, '[REDACTED]')
        // Redact sensitive keywords
        .replace(/password|secret|token|api[_-]?key/gi, '[REDACTED]');
    })
    // Filter out environment variable access
    .filter(line => {
      return !line.includes('process.env') && !line.includes('Deno.env');
    })
    .join('\n')
    // Limit total length (performance budget)
    .slice(0, 500);
}
